import { useState, useEffect } from "react";
import axios, { AxiosResponse, AxiosError } from "axios";
import { apibaseurl, jwttoken } from "../../environment";
import { useNavigate } from "react-router";

interface ApiOptions {
  method?: "GET" | "POST" | "DELETE" | "PUT";
  payload?: Record<string, any> | null;
  autoCall?: boolean;
  dependencies?: any[];
}

interface UseApiResult<T> {
  data: T | null;
  loading: boolean;
  error: any;
  callApi: (
    payload?: Record<string, any>,
    customUrl?: string,
  ) => Promise<T | undefined>;
  refetch: () => Promise<T | undefined>;
}

function useApi<T = any>(
  endpoint: string,
  {
    method = "GET",
    payload = null,
    autoCall = true,
    dependencies = [],
  }: ApiOptions = {},
): UseApiResult<T> {
  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState<boolean>(autoCall);
  const [error, setError] = useState<any>(null);
  const navigate = useNavigate();

  const callApi = async (
    customPayload = payload,
    customUrl?: string,
  ): Promise<T | undefined> => {
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${
            (jwttoken ?? localStorage.getItem("token")) || ""
          }`,
          "X-Client-Type": "web",
        },
        timeout: 20000,
        data: method === "DELETE" ? customPayload : undefined,
      };

      let response: AxiosResponse<any>;
      const finalUrl = `${apibaseurl}${customUrl ?? endpoint}`;
      if (method === "POST") {
        response = await axios.post(
          `${apibaseurl}${endpoint}`,
          customPayload,
          config,
        );
      } else if (method === "PUT") {
        response = await axios.put(finalUrl, customPayload, config);
      } else if (method === "DELETE") {
        response = await axios.delete(`${finalUrl}`, config);
      } else {
        // response = await axios.get(`${apibaseurl}${endpoint}`, config);
        response = await axios.get(`${finalUrl}`, config);
      }

      setData(response.data);
      setError(null);
      return response.data;
    } catch (err: unknown) {
      console.error("API Error:", err);
      setError(err);
      if (axios.isAxiosError(err)) {
        const status = err.response?.status;
        if (status === 401) {
          localStorage.removeItem("token");
          navigate("/login");
          return;
        }
        if (err.code === "ECONNABORTED") {
          navigate("/timeout");
        } else if (err.message === "Network Error") {
          navigate("/no-internet");
        } else if (err.response?.status === 500) {
          navigate("/error-500");
        } else {
          throw err;
        }
      } else {
        throw err;
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (autoCall) {
      callApi();
    }
  }, dependencies);

  return { data, loading, error, callApi, refetch: callApi };
}

export default useApi;
